import { Box, CircularProgress, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, Skeleton } from '@/components';
import { ComplianceTypes, Routes } from '@/constants';
import { Link } from '@/containers';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  RefetchOnMountDelay,
  selectIsMemberAdmin,
  selectSessionUserId,
  selectUserCompliance,
  useFetchOrganizationMembersCountQuery,
  useFetchPlanLimitsQuery,
  useFetchPlanQuery,
} from '@/store';
import { PurchaseType } from '@/types';

const PlanTypeMap = {
  [PurchaseType.FREE_TRIAL]: 'Free Trial',
  [PurchaseType.B2C_PAID]: 'Personal',
  [PurchaseType.B2B_PAID]: 'Team',
  [PurchaseType.B2B_FREE]: 'Team Trial',
  [PurchaseType.PAY_AS_YOU_GO]: 'Team',
  [PurchaseType.TEAM_TRIAL]: 'Team Trial',
  [PurchaseType.TEAM_PAID]: 'Team',
} as const;

const getMyLearningText = ({
  plan,
  compliance = ComplianceTypes.CPE,
  totalCredits,
}: {
  plan: ReturnType<typeof useFetchPlanQuery>['data'];
  compliance?: (typeof ComplianceTypes)[keyof typeof ComplianceTypes];
  totalCredits?: number;
}) => {
  if (plan.showUsageRemaining) {
    return `${Math.round((plan.usageRemaining || 0) * 10) / 10}
      ${
        plan.purchaseType === PurchaseType.FREE_TRIAL ||
        plan.purchaseType === PurchaseType.B2B_FREE ||
        plan.purchaseType === PurchaseType.TEAM_TRIAL
          ? '/' + (plan.cap || totalCredits || 1)
          : ''
      }
      ${compliance === ComplianceTypes.CPE ? 'Credits' : 'Hours'} Remaining`;
  }
  if (
    plan.purchaseType === PurchaseType.FREE_TRIAL ||
    plan.purchaseType === PurchaseType.TEAM_TRIAL ||
    totalCredits
  ) {
    return `${Math.round((plan.totalRedemptions || 0) * 10) / 10} ${compliance === ComplianceTypes.CPE ? 'Credits Redeemed' : 'Hours Redeemed'}`;
  }
  return 'Unlimited Access';
};

export const PlanInfo = () => {
  const navigate = useNavigate();
  const userId = useSelector(selectSessionUserId);
  const compliance = useSelector(selectUserCompliance);
  const { data: plan, isLoading: isPlanLoading } = useFetchPlanQuery(
    userId ? undefined : skipToken,
    {
      refetchOnMountOrArgChange: RefetchOnMountDelay,
      refetchOnFocus: true,
    },
  );
  const isMemberAdmin = useSelector(selectIsMemberAdmin);
  const { data: planLimits, isLoading: isPlanLimitsLoading } =
    useFetchPlanLimitsQuery(isMemberAdmin ? undefined : skipToken, {
      refetchOnMountOrArgChange: RefetchOnMountDelay,
      refetchOnFocus: true,
    });
  const { data: membersCount, isLoading: isMemberLoading } =
    useFetchOrganizationMembersCountQuery(
      isMemberAdmin ? undefined : skipToken,
    );

  const isLoading = isPlanLoading || isPlanLimitsLoading || isMemberLoading;
  const validUntil = plan?.validUntil
    ? new Date(plan.validUntil)
    : isMemberAdmin && plan?.unenforcedLimits?.validUntil
      ? new Date(plan.unenforcedLimits.validUntil)
      : undefined;
  const totalCredits = plan?.totalCredits
    ? plan.totalCredits
    : isMemberAdmin && plan?.unenforcedLimits?.totalCredits
      ? plan.unenforcedLimits.totalCredits
      : undefined;

  if (!plan && !isLoading) {
    return null;
  }

  return (
    <Box
      sx={{
        bgcolor: 'grey50',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          py: 1.5,
          px: 2,
          backgroundColor: 'primary.light',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Box>
          <Typography variant="body2" color="text.contrast">
            Plan
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1.5,
              alignItems: 'center',
            }}>
            {!plan || isLoading ? (
              <CircularProgress
                size={36}
                sx={{ mt: 0.5, color: 'text.contrast' }}
              />
            ) : (
              <Typography variant="h2" sx={{ mt: 0.5 }} color="text.contrast">
                {PlanTypeMap[plan.purchaseType]}
              </Typography>
            )}
            {isMemberAdmin && (
              <Box
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'text.contrast',
                  width: 50,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Typography
                  variant="body3SemiBold"
                  sx={{
                    color: 'text.contrast',
                  }}>
                  Admin
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {plan?.purchaseType === PurchaseType.FREE_TRIAL && (
          <Button
            variant="secondary"
            skinny
            label={'Explore Plans'}
            fullWidth={false}
            onClick={() => {
              AnalyticsService.pageViewPricing({
                location: {
                  page: EVENT_CONSTANTS.PAGE.PROFILE,
                  section: EVENT_CONSTANTS.SECTION.PLAN_INFO,
                  button: 'explore_plans',
                },
              });
              navigate(Routes.PRICING);
            }}
          />
        )}
      </Box>
      <Box sx={{ p: 3 }}>
        {!plan || isLoading ? (
          <Box>
            <Box>
              <Skeleton width="80%" height={21} />
            </Box>
            <Box>
              <Skeleton width="100%" height={21} sx={{ mt: 2 }} />
            </Box>
          </Box>
        ) : plan.licenceValid ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
            }}>
            {validUntil && (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                <Typography variant="body2SemiBold">
                  {plan.purchaseType === PurchaseType.B2B_FREE ||
                  plan.purchaseType === PurchaseType.FREE_TRIAL ||
                  plan.purchaseType === PurchaseType.TEAM_TRIAL
                    ? 'Trial Ends:'
                    : 'Subscription Renews:'}{' '}
                </Typography>
                <Typography variant="body2">
                  {format(new Date(validUntil), 'MMM d, yyyy')}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
                <Typography variant="body2SemiBold">My Learning:</Typography>
                <Typography variant="body2">
                  {getMyLearningText({ plan, compliance, totalCredits })}
                </Typography>
              </Box>
              {(plan.purchaseType === PurchaseType.FREE_TRIAL ||
                plan.purchaseType === PurchaseType.B2B_FREE ||
                plan.purchaseType === PurchaseType.TEAM_TRIAL) &&
              plan.showUsageRemaining ? (
                <Box
                  sx={{
                    flexGrow: 1,
                    bgcolor: 'white',
                    height: 6,
                  }}>
                  <Box
                    sx={{
                      width: `${((plan.usageRemaining || 0) / (plan.cap || totalCredits || 1)) * 100}%`,
                      height: '100%',
                      bgcolor: 'palette.secondary.active',
                      borderRadius: 1,
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ flexGrow: 1 }} />
              )}
            </Box>
            {isMemberAdmin && totalCredits && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
                  <Typography variant="body2SemiBold">
                    Team Learning:
                  </Typography>
                  <Typography variant="body2">
                    {Math.round((planLimits?.usedCredits || 0) * 10) / 10}/
                    {totalCredits}{' '}
                    {compliance === ComplianceTypes.CPE ? 'Credits' : 'Hours'}{' '}
                    Used
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    bgcolor: 'white',
                    height: 6,
                  }}>
                  <Box
                    sx={{
                      width: `${Math.min(((planLimits?.usedCredits || 0) / totalCredits) * 100, 100)}%`,
                      height: '100%',
                      bgcolor:
                        plan.totalRedemptions > totalCredits
                          ? 'salmon'
                          : 'secondary.active',
                      borderRadius: 1,
                    }}
                  />
                </Box>
              </Box>
            )}
            {membersCount ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
                  <Typography variant="body2SemiBold">Team Members:</Typography>
                  <Typography variant="body2">
                    {membersCount}
                    {planLimits?.seatCap?.seatCap
                      ? `/${planLimits.seatCap.seatCap}`
                      : ''}
                  </Typography>
                </Box>
                <Button
                  variant="secondary"
                  label="See Reporting"
                  fullWidth={false}
                  sx={{
                    bgcolor: 'white',
                  }}
                  href={Routes.REPORTING}
                />
              </Box>
            ) : null}
          </Box>
        ) : (
          <>
            {plan.purchaseType === PurchaseType.FREE_TRIAL && (
              <Typography variant="body2SemiBold" sx={{ color: 'salmon' }}>
                Your trial has ended, upgrade now to continue enjoying LumiQ
              </Typography>
            )}
            {plan.purchaseType === PurchaseType.B2C_PAID && (
              <Typography variant="body2SemiBold" sx={{ color: 'salmon' }}>
                It appears your plan has expired, or your usage limit has been
                reached
              </Typography>
            )}
            {(plan.purchaseType === PurchaseType.B2B_FREE ||
              plan.purchaseType === PurchaseType.TEAM_TRIAL) && (
              <>
                <Typography variant="body2SemiBold" sx={{ color: 'salmon' }}>
                  Your team trial has ended
                </Typography>
                {isMemberAdmin ? (
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, color: 'secondary.main' }}>
                    <Link to={`${Routes.PRICING}?highlighted_plan=${'team'}`}>
                      View available plans
                    </Link>
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Contact your plan administrator
                  </Typography>
                )}
              </>
            )}
            {(plan.purchaseType === PurchaseType.B2B_PAID ||
              plan.purchaseType === PurchaseType.PAY_AS_YOU_GO ||
              plan.purchaseType === PurchaseType.TEAM_PAID) && (
              <>
                <Typography variant="body2SemiBold" sx={{ color: 'salmon' }}>
                  {plan.validUntil && new Date(plan.validUntil) < new Date()
                    ? 'It appears your plan has expired'
                    : 'Your usage limit has been reached'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Contact your plan administrator
                </Typography>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
